// frontend/src/pages/MaintenancePage.js
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  alpha,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// Icons
import BuildIcon from '@mui/icons-material/Build';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';

const MaintenancePage = () => {
  const theme = useTheme();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 4
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              background: 'white'
            }}
          >
            {/* Animated Icon */}
            <motion.div
              animate={{
                rotate: [0, 10, -10, 10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  mb: 3
                }}
              >
                <BuildIcon
                  sx={{
                    fontSize: 64,
                    color: theme.palette.primary.main
                  }}
                />
              </Box>
            </motion.div>

            {/* Title */}
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              fontWeight="bold"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Wartungsmodus
            </Typography>

            {/* Subtitle */}
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Das System befindet sich derzeit im Wartungsmodus
            </Typography>

            {/* Message */}
            <Box
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                background: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}
            >
              <Typography variant="body1" color="text.primary" paragraph>
                Wir führen gerade Wartungsarbeiten durch, um dir ein besseres Erlebnis zu bieten.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser ? (
                  <>
                    Du bist als <strong>{currentUser.username}</strong> angemeldet.
                    Bitte versuche es in Kürze erneut.
                  </>
                ) : (
                  'Bitte versuche es in Kürze erneut.'
                )}
              </Typography>
            </Box>

            {/* User Info for Admins and Devs */}
            {['admin', 'dev'].includes(currentUser?.role) && (
              <Box
                sx={{
                  p: 2,
                  mb: 4,
                  borderRadius: 2,
                  background: alpha(theme.palette.warning.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                }}
              >
                <Typography variant="body2" color="text.primary" fontWeight="medium">
                  👨‍💼 Administrator-Hinweis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Du kannst den Wartungsmodus in den Systemeinstellungen deaktivieren.
                </Typography>
              </Box>
            )}

            {currentUser?.role === 'dev' && (
              <Box
                sx={{
                  p: 2,
                  mb: 4,
                  borderRadius: 2,
                  background: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                }}
              >
                <Typography variant="body2" color="text.primary" fontWeight="medium">
                  👨‍💻 Entwickler-Hinweis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Als Entwickler hast du vollen Zugriff während des Wartungsmodus.
                  Du wirst nicht automatisch weitergeleitet.
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Seite aktualisieren
              </Button>

              {currentUser && (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Abmelden
                </Button>
              )}
            </Box>

            {/* Footer Note */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 4,
                display: 'block'
              }}
            >
              Bei Fragen wende dich bitte an deinen Administrator.
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MaintenancePage;
